import { ComponentPropsWithoutRef, createElement, ElementType } from 'react';
import { twJoin } from 'tailwind-merge';

import {
  DISABLED_CLASSES,
  STATUS_BORDER_CLASSES,
} from '@/core/tamagoshiTailwind/components/Input/utils/BaseInputTypes';
import { ChevronDownIcon } from '@/core/tamagoshiTailwind/icons/ChevronDownIcon';
import { TamagoshiIconComponent } from '@/core/tamagoshiTailwind/interfaces';
import { twMerge } from '@/core/tamagoshiTailwind/util/twMerge';

const DEFAULT_TAG = 'div' as const;

type BaseInputContainerProps<
  T extends ElementType<{ className: string }> = typeof DEFAULT_TAG,
> = {
  as?: T;
} & ComponentPropsWithoutRef<T>;

function BaseInputContainer<
  T extends ElementType<{ className: string }> = typeof DEFAULT_TAG,
>({ as, className, ...props }: BaseInputContainerProps<T>) {
  return createElement(as ?? DEFAULT_TAG, {
    ...props,
    className: twMerge(
      `absolute top-0 flex h-xxxl w-xxl items-center first:left-0
      first:justify-end last:right-0 last:justify-start
      [&:nth-last-child(2)]:right-0 [&:nth-last-child(2)]:justify-start`,
      className,
    ),
  });
}

type BaseInputIconProps<T extends ElementType = typeof DEFAULT_TAG> = {
  icon: TamagoshiIconComponent;
  color?: string;
} & BaseInputContainerProps<T>;

export function BaseInputIcon<T extends ElementType = typeof DEFAULT_TAG>({
  icon: Icon,
  color,
  ...props
}: BaseInputIconProps<T>) {
  return (
    <BaseInputContainer {...(props as BaseInputContainerProps<T>)}>
      <Icon className={twJoin('size-[24px]', color)} />
    </BaseInputContainer>
  );
}

type BaseInputDropdownProps = {
  icon: TamagoshiIconComponent;
  color?: string;
  isOpen: boolean;
} & BaseInputContainerProps<'button'>;

export function BaseInputDropdown({
  icon: Icon,
  color,
  isOpen,
  ...props
}: BaseInputDropdownProps) {
  return (
    <button
      {...props}
      className={twMerge(
        `flex w-[64px] items-center justify-center gap-xs rounded-l-card
        border-y border-l px-s focus:z-10 disabled:cursor-not-allowed`,
        STATUS_BORDER_CLASSES,
        DISABLED_CLASSES,
        props.className,
      )}
      type="button"
    >
      <Icon className={twJoin('size-[20px]', color)} />
      <ChevronDownIcon
        className={twJoin(
          `text-foundation-primary transition-transform
          group-disabled:text-foundation-disabled`,
          isOpen && 'rotate-180 transform',
        )}
      />
    </button>
  );
}
